import { useQuery } from '@tanstack/react-query'
import { Address, zeroAddress } from 'viem'
import { publicEulerRoutes } from '@/lib/euler-api/publicRouteSpecs'

export interface ReallocateWithdrawal {
  caller: string
  chainId: number
  id: string
  lastUpdated: string
  strategy: string
  timestamp: string
  txHash: string
  vault: string
  withdrawnAssets: string
  withdrawnAssetsUSD: number
  withdrawnAssetsUSDBigInt: string
  withdrawnShares: string
}

export interface ReallocateDeposit {
  caller: string
  chainId: number
  id: string
  lastUpdated: string
  strategy: string
  suppliedAssets: string
  suppliedAssetsUSD: number
  suppliedAssetsUSDBigInt: string
  suppliedShares: string
  timestamp: string
  txHash: string
  vault: string
}

export interface EarnVaultResponse {
  vault: {
    id: string
    chainId: number
    createdAt: string
    createdBlock: string
    vault: string
    timestamp: string
    vaultName: string | null
    vaultSymbol: string | null
    vaultDecimals: number | null
    asset: string | null
    assetName: string | null
    assetSymbol: string | null
    assetDecimals: number | null
    totalShares: string
    totalAssets: string
    lostAssets: string
    performanceFee: string
    feeReceiver: string | null
    creator: string | null
    curator: string | null
    owner: string | null
    guardian: string | null
    evc: string | null
    permit2: string | null
    lastBlockOfUpdate: number | null
    lastUpdated: string
    supplyQueue: string[] | null
    availableAssets: string
    timelock: string
    totalAssetsUSDBigInt: string
    totalAssetsUSD: number
    availableAssetsUSDBigInt: string
    availableAssetsUSD: number
    strategies: Array<{
      id: string
      chainId: number
      vault: string
      strategy: string
      timestamp: string
      lastBlockOfUpdate: string
      allocatedAssets: string
      sharesAllocated: string
      currentAllocationCap: string
      pendingAllocationCap: string
      pendingAllocationCapValidAt: string
      removableAt: string
      deleted: boolean
      lastUpdated: string
      allocatedAssetsUSD: number
      allocatedAssetsUSDBigInt: string
      strategyVaultName: string | null
      strategyVaultSymbol: string | null
      strategyVaultDecimals: number | null
      strategyAsset: string | null
      status: 'active' | 'inactive' | 'pendingRemoval'
    }>
    apyCurrent: number | null
  }
  reallocateWithdrawals: ReallocateWithdrawal[]
  reallocateDeposits: ReallocateDeposit[]
  apy7d: number | null
  apy30d: number | null
  apy90d: number | null
  apyCurrent: number | null
}

export function useEarnVaultIndexer<TData = EarnVaultResponse>({
  chainId,
  address,
  select,
  enabled = true,
}: {
  chainId: number
  address: Address
  select?: (data: EarnVaultResponse) => TData
  enabled?: boolean
}) {
  return useQuery<EarnVaultResponse, Error, TData>({
    queryKey: ['earnVaultsIndexer', chainId, address],
    queryFn: async () => {
      const url = publicEulerRoutes.earnVault.clientUrl(chainId ?? 0, {
        vaultAddress: address?.toString() ?? '0x0000000000000000000000000000000000000000',
      })
      const response = await fetch(url)
      if (!response.ok) {
        throw new Error(`Failed to fetch vault data: ${response.statusText}`)
      }
      return response.json()
    },
    select: (data) => (select ? select(data) : (data as TData)),
    enabled: enabled && !!chainId && !!address && address !== zeroAddress,
    staleTime: 30_000, // Cache for 30 seconds
    retry: 1, // Only retry once if indexer fails
    retryDelay: 500, // Wait 500ms before retry
    refetchOnMount: true, // Always refetch on component mount
  })
}
